import { __esDecorate, __runInitializers } from "tslib";
import { StringTag } from '#library/decorator.library.js';
/**
 * A lightweight `Set`-compatible container that delegates `has()` lookups to a
 * parent Set/ScopedSet, but confines `add()` writes to its own-local storage.
 *
 * This mirrors JavaScript prototype-chain semantics:
 * - Values added to the parent are **visible** to the child via `has()`.
 * - Values added to the child are **isolated** from the parent;
 *   the parent Set is never modified by child `add()` calls.
 *
 * @example
 * const global = new Set(['a']);
 * const sandbox = new ScopedSet(global);
 * sandbox.has('a');   // true  (inherited from parent)
 * sandbox.add('b');
 * sandbox.has('b');   // true  (own-local)
 * global.has('b');    // false (never written to parent)
 */
let ScopedSet = (() => {
    let _classDecorators = [StringTag('ScopedSet')];
    let _classDescriptor;
    let _classExtraInitializers = [];
    let _classThis;
    var ScopedSet = class {
        static { _classThis = this; }
        static {
            const _metadata = typeof Symbol === "function" && Symbol.metadata ? Object.create(null) : void 0;
            __esDecorate(null, _classDescriptor = { value: _classThis }, _classDecorators, { kind: "class", name: _classThis.name, metadata: _metadata }, null, _classExtraInitializers);
            ScopedSet = _classThis = _classDescriptor.value;
            if (_metadata) Object.defineProperty(_classThis, Symbol.metadata, { enumerable: true, configurable: true, writable: true, value: _metadata });
            __runInitializers(_classThis, _classExtraInitializers);
        }
        #own = new Set();
        #parent;
        constructor(parent) {
            this.#parent = parent;
        }
        /** `true` if value is in own-local storage OR anywhere in the parent chain. */
        has(value) {
            return this.#own.has(value) || (this.#parent?.has(value) ?? false);
        }
        /** Adds to own-local storage only — never propagates to the parent. */
        add(value) {
            this.#own.add(value);
            return this;
        }
        /** Removes from own-local storage only. */
        delete(value) {
            return this.#own.delete(value);
        }
        /** Clears own-local storage only. */
        clear() {
            this.#own.clear();
        }
        /** Number of own-local entries (does not include parent entries). */
        get size() { return this.#own.size; }
        forEach(cb, thisArg) {
            this.#own.forEach((val1, val2) => cb.call(thisArg, val1, val2, this));
        }
        values() { return this.#own.values(); }
        keys() { return this.#own.keys(); }
        entries() { return this.#own.entries(); }
        [Symbol.iterator]() { return this.#own[Symbol.iterator](); }
    };
    return ScopedSet = _classThis;
})();
export { ScopedSet };
